import { useEffect, useRef, useState } from 'react';

interface Props {
  /** 'verify' = unlock an existing PIN. 'set' = first-time, asks twice for confirm. */
  mode: 'verify' | 'set';
  /** Title shown above the keypad. */
  title: string;
  /** Subtitle line; helpful for naming the character being unlocked. */
  subtitle?: string;
  /** Inline error string (e.g., "wrong PIN, 3 attempts remaining"). */
  error?: string | null;
  /** When true, keypad is locked and submit is hidden. */
  locked?: boolean;
  /** Lockout countdown in seconds. Drives the live countdown shown to
   *  the user; when it reaches 0, onLockoutEnd fires (if provided). */
  lockedSecondsRemaining?: number;
  /** Fires when the local lockout countdown ticks down to 0. The parent
   *  can use this to flip `locked` back off so the keypad re-enables. */
  onLockoutEnd?: () => void;
  /** Called with the entered 4-digit string once confirmed. */
  onSubmit: (pin: string) => void;
  onCancel: () => void;
  busy?: boolean;
  /** Mandatory mode (owner: PIN required at start): hides the cancel
   *  button and disables backdrop dismissal — the only way out is a PIN. */
  mandatory?: boolean;
  /** Override text for the cancel button (defaults to "Cancel"). */
  cancelLabel?: string;
}

const SLOTS = 4;

export function PinPad({ mode, title, subtitle, error, locked, lockedSecondsRemaining, onLockoutEnd, onSubmit, onCancel, busy, cancelLabel, mandatory = false }: Props) {
  const [first, setFirst] = useState('');
  const [confirm, setConfirm] = useState('');
  const [stage, setStage] = useState<'first' | 'confirm'>('first');
  const [localError, setLocalError] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number>(lockedSecondsRemaining ?? 0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Reset confirm whenever the parent surfaces a new error (e.g., wrong PIN
  // on verify) — the user is going to try again from scratch.
  useEffect(() => {
    if (error) {
      setFirst('');
      setConfirm('');
      setStage('first');
      setLocalError(null);
      inputRef.current?.focus();
    }
  }, [error]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  // Sync remaining whenever the parent surfaces a fresh lockout duration.
  // We re-seed local state so successive lock errors reset the countdown.
  useEffect(() => {
    setRemaining(lockedSecondsRemaining ?? 0);
  }, [lockedSecondsRemaining]);

  // Live tick the lockout timer. When it hits 0 we notify the parent so
  // it can clear `locked` and let the user try again.
  useEffect(() => {
    if (!locked) return;
    if (remaining <= 0) return;
    const id = window.setInterval(() => {
      setRemaining((r) => {
        const next = Math.max(0, r - 1);
        if (next === 0) {
          // Defer the callback so we don't update parent state during a
          // setInterval tick that's already inside React's setState path.
          window.setTimeout(() => onLockoutEnd?.(), 0);
        }
        return next;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [locked, remaining, onLockoutEnd]);

  const current = stage === 'first' ? first : confirm;
  const setCurrent = stage === 'first' ? setFirst : setConfirm;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (locked || busy) return;
    const v = e.target.value.replace(/[^0-9]/g, '').slice(0, SLOTS);
    setCurrent(v);
    setLocalError(null);
    if (v.length === SLOTS) {
      // auto-advance / auto-submit
      if (mode === 'verify') {
        onSubmit(v);
      } else if (stage === 'first') {
        setStage('confirm');
        // refocus on next tick
        setTimeout(() => inputRef.current?.focus(), 10);
      } else {
        if (v === first) onSubmit(v);
        else {
          setLocalError('PINs did not match. Try again.');
          setFirst('');
          setConfirm('');
          setStage('first');
          setTimeout(() => inputRef.current?.focus(), 10);
        }
      }
    }
  }

  const showError = error ?? localError;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget && !mandatory) onCancel(); }}
    >
      <div
        className="pinpad-card"
        style={{
          minWidth: 320, maxWidth: 380, padding: '24px 22px',
          borderRadius: 14,
          background: 'var(--bg-elev, #1a1410)',
          border: '1px solid rgba(255,200,120,0.18)',
          boxShadow: '0 30px 70px rgba(0,0,0,0.55)',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <h2 className="display" style={{ margin: 0, fontSize: 22, textAlign: 'center' }}>{title}</h2>
        {subtitle && <p className="muted" style={{ margin: 0, textAlign: 'center', fontSize: 13 }}>{subtitle}</p>}
        {mode === 'set' && !locked && (
          <p className="muted" style={{ margin: 0, textAlign: 'center', fontSize: 12 }}>
            {stage === 'first' ? 'Choose a 4-digit PIN.' : 'Enter it again to confirm.'}
          </p>
        )}

        {/* R1#6 — iOS Safari refuses to open the soft keyboard on
            programmatic focus that wasn't the direct tap target. The
            visible slot row is rendered as a non-interactive background
            layer; the real <input> sits on top, transparent but
            FULLY clickable, so the user's tap lands directly on the
            input element and triggers the system keyboard naturally.
            fontSize:16 is non-negotiable — anything smaller triggers
            iOS auto-zoom on focus. */}
        <div
          style={{
            position: 'relative',
            height: 48,
            margin: '10px 0',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              position: 'absolute', inset: 0,
              display: 'flex', justifyContent: 'center', gap: 12,
              pointerEvents: 'none',
            }}
          >
            {Array.from({ length: SLOTS }).map((_, i) => (
              <span
                key={i}
                style={{
                  width: 38, height: 48, borderRadius: 8,
                  border: '1px solid rgba(255,200,120,0.25)',
                  background: 'rgba(0,0,0,0.35)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22, fontWeight: 700, fontFamily: 'monospace',
                  color: 'var(--ink, #f0e6d2)',
                }}
              >
                {current[i] ? '•' : ''}
              </span>
            ))}
          </div>
          <input
            ref={inputRef}
            type="tel"
            inputMode="numeric"
            autoComplete="off"
            pattern="[0-9]*"
            maxLength={SLOTS}
            value={current}
            onChange={handleChange}
            disabled={locked || busy}
            aria-label="PIN entry"
            style={{
              position: 'absolute', inset: 0, width: '100%', height: '100%',
              opacity: 0,
              // Critical: input must take pointer events directly so iOS
              // accepts the tap as a user gesture and opens the keyboard.
              pointerEvents: 'auto',
              // 16px font prevents iOS from auto-zooming on focus.
              fontSize: 16,
              border: 'none', outline: 'none', background: 'transparent',
              color: 'transparent',
              caretColor: 'transparent',
              // Stop the OS-rendered native input chrome from leaking
              // through any way (paddings, autofill yellow flash, etc).
              padding: 0, margin: 0,
              // Disable text selection UI so the phantom long-press on
              // an invisible input doesn't show selection handles.
              WebkitUserSelect: 'none',
              userSelect: 'none',
            }}
          />
        </div>

        {showError && (
          <div role="alert" className="gate-rejection" style={{ textAlign: 'center', fontSize: 13 }}>
            {showError}
          </div>
        )}
        {locked && (
          <div role="alert" className="gate-rejection" style={{ textAlign: 'center', fontSize: 13 }}>
            Locked. Try again in {
              remaining > 0
                ? (remaining > 60 ? `${Math.ceil(remaining / 60)}m` : `${remaining}s`)
                : 'a bit'
            }.
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 4, justifyContent: 'center' }}>
          {!mandatory && (
            <button type="button" className="btn ghost" onClick={onCancel}>{cancelLabel ?? 'Cancel'}</button>
          )}
        </div>
      </div>
    </div>
  );
}
